import OrderDocumentCompletenessSection from '../modules/orders/sections/OrderDocumentCompletenessSection';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs, { type Dayjs } from 'dayjs';
import type { MenuProps } from 'antd';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import SupplierOrderDraftReviewBanner from '../modules/orders/sections/SupplierOrderDraftReviewBanner';
import SupplierOrderChooseSupplierModal from '../modules/orders/sections/SupplierOrderChooseSupplierModal';
import SupplierOrderDraftRejectModal from '../modules/orders/sections/SupplierOrderDraftRejectModal';
import SupplierOrderDraftStagePanel from '../modules/orders/supplierOrderStage/SupplierOrderDraftStagePanel';
import SupplierOrderMoreDataSection from '../modules/orders/supplierOrderStage/SupplierOrderMoreDataSection';
import SupplierOrderNewDraftCard from '../modules/orders/supplierOrderStage/SupplierOrderNewDraftCard';
import SupplierOrderStagePanel from '../modules/orders/supplierOrderStage/SupplierOrderStagePanel';
import SupplierOrderStageRail from '../modules/orders/supplierOrderStage/SupplierOrderStageRail';
import SupplierOrderDeliveries from '../modules/orders/supplierOrderStage/SupplierOrderDeliveries';
import SupplierOrderDeliveryTransportsSection from '../modules/orders/sections/SupplierOrderDeliveryTransportsSection';
import SupplierOrderDeliveryAside from '../modules/orders/supplierOrderStage/SupplierOrderDeliveryAside';
import { canPlanSupplierOrderTransport } from '../modules/orders/supplierOrderStage/canPlanSupplierOrderTransport';
import SupplierOrderCostCard from '../modules/orders/supplierOrderStage/SupplierOrderCostCard';
import SupplierOrderTransportStrip from '../modules/orders/supplierOrderStage/SupplierOrderTransportStrip';
import { buildSupplierOrderCostSummary } from '../modules/orders/supplierOrderStage/supplierOrderCost';
import SupplierOrderOriginCard from '../modules/orders/supplierOrderStage/SupplierOrderOriginCard';
import SupplierOrderPurchaseInvoiceModal from '../modules/orders/sections/SupplierOrderPurchaseInvoiceModal';
import useSupplierOrderStageData from '../modules/orders/supplierOrderStage/useSupplierOrderStageData';
import { SUPPLIER_ORDER_STAGES, getSupplierOrderStageIndex } from '../modules/orders/supplierOrderStage/supplierOrderDraftStage';
import {
  buildSupplierOrderStageModel,
  STAGE_RETURN_STATUS,
  type SupplierOrderStageActionKey,
} from '../modules/orders/supplierOrderStage/supplierOrderStageModel';
import { withTransportReturnContext } from '../modules/transports/transportReturnContext';
import type { SupplierOrderTransportDto } from '../dto/transport';
import {
  Alert,
  App,
  Button,
  Card,
  Collapse,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Dropdown,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Timeline,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  DollarOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  HomeOutlined,
  MoreOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  ShopOutlined,
  TruckOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { authService } from '../services/auth';
import { formatDateEuropean, formatDateTimeEuropean } from '../utils/dateUtils';
import { buildSafePdfFileName } from '../utils/pdfFileName';
import { getDisplayOrderNumber, getDisplayOrderNumberAfterUpdate } from '../types/order';
import { usePageHeaderTitleOverride } from '../modules/navigation/pageHeaderTitleOverride';
import { ORDER_CURRENCIES } from '../modules/orders/currencies';
import {
  SUPPLIER_ORDER_PROGRESS_STATUSES,
  SUPPLIER_ORDER_STATUSES,
  SupplierOrderStatus,
} from '../modules/orders/supplierOrderStatuses';
import OrderStatusTransitionSelect from '../modules/orders/components/OrderStatusTransitionSelect';
import { FactoryOptionDto, SupplierOptionDto } from '../dto/reference';
import {
  SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES,
  SupplierOrderStatusHistoryDto,
  supplierOrderAmountRule,
  supplierOrderNumberRule,
} from '../dto/supplierOrder';
import { fetchFactoryOptions, fetchSupplierOptions } from '../modules/reference-data/api/referenceDataApi';
import { fetchStockValuation, type StockValuation } from '../modules/orders/stockValuation/stockValuationApi';
import { mergeSupplierOrderHistory } from '../modules/orders/supplierOrderHistory';
import {
  downloadSupplierOrderPdf,
  getSupplierOrderDisplayNumber,
  unarchiveSupplierOrder,
  updateSupplierOrder,
} from '../modules/orders/api/supplierOrdersApi';
import { useSupplierOrderDetail } from '../modules/orders/queries/useSupplierOrderDetail';
import { getSupplierOrderSummary } from '../modules/orders/components/SupplierOrderSummaryPreview';
import { canManageTransportRoutes } from '../modules/transports/transportRouteAccessPolicy';
import { resolveSupplierOrderLoadingAddress } from '../modules/transports/transportKindAddresses';
import {
  buildSupplierOrderMoreDataUpdateData,
  buildSupplierOrderUpdateData,
  getSupplierOrderFieldNames,
  getSupplierOrderInitialValues,
  getSupplierOrderMoreDataInitialValues,
  SupplierOrderEditableField,
  SupplierOrderMoreDataValues,
} from '../modules/orders/supplierOrderDetailEditUtils';
import {
  OrderWorkflowRail,
  PAGE_SHELL_STYLE,
  SECTION_CARD_BODY_STYLE,
  SECTION_CARD_STYLE,
} from '../modules/orders/orderDetailLayout';
import { InvoiceCoverageTag } from '../modules/invoices/components/InvoiceCoverageSummary';
import { SupplierOrderItemsSection } from '../modules/orders/sections/SupplierOrderItemsSection';
import { promptOrderPdfConversionUnit } from '../modules/orders/orderPdfConversionPrompt';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../utils/polishNumberInput';
import { useFeedback } from '../utils/feedback';
import {
  incompleteValuationWarning,
  readIncompleteValuationIssues,
  staleTransportSplitWarning,
  transportDetachFailedWarning,
} from '../modules/orders/stockValuationSaveWarning';
import {
  getSupplierOrderErrorMessage,
  getSupplierOrderNumberTaken,
  isBlockingSupplierOrderError,
  type SupplierOrderNumberTaken,
} from '../modules/orders/supplierOrderErrorMessages';
import SupplierOrderNumberTakenHelp from '../modules/orders/components/SupplierOrderNumberTakenHelp';
// Shared detail-page theme (status badge inks, financial strip): the classes
// are order-/supplier-neutral under .order-detail-page, see OrderDetail.css.
import { useBackNavigation } from '../hooks/useBackNavigation';
import './OrderDetail.css';
// The ZD header follows the ZK card rules (zk-head / zk-chip), see modules/orders/process/DESIGN.md.
import '../modules/orders/process/OrderProcess.css';

const { Text } = Typography;

const SUPPLIER_ORDERS_LIST_PATH = '/app/orders?orderFlow=supplier';
/**
 * Supplier-order detail page. It reuses the customer-order layout primitives,
 * but copy and visible fields stay supplier-side: supplier, factory, purchase
 * documents and landed costs. Every ZD is a stock purchase.
 */
const SupplierOrderDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const goBack = useBackNavigation();
  const { t } = useTranslation(['orders', 'common', 'tasks', 'transport']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [editForm] = Form.useForm();
  const [statusForm] = Form.useForm();

  const { supplierOrder, loading, refresh } = useSupplierOrderDetail(id);
  // Hoisted so every handler below (edit/status/link/document actions) can
  // name the order in its toast without re-deriving it — mirrors OrderDetail's
  // `orderDisplayNumber`. Guarded by `?` since it's read before the not-found check.
  const displayOrderNumber = supplierOrder ? getSupplierOrderDisplayNumber(supplierOrder) : '';

  usePageHeaderTitleOverride(
    supplierOrder
      ? t('orders:pageHeader.supplier', { number: getDisplayOrderNumber(supplierOrder) })
      : null,
  );

  const [editingField, setEditingField] = useState<SupplierOrderEditableField | null>(null);
  const [savingField, setSavingField] = useState<SupplierOrderEditableField | null>(null);
  const [numberForm] = Form.useForm();
  const [numberModalOpen, setNumberModalOpen] = useState(false);
  const [numberTaken, setNumberTaken] = useState<SupplierOrderNumberTaken | null>(null);
  const numberFormUseManual = Form.useWatch('useManualNumbering', numberForm);
  const [suppliers, setSuppliers] = useState<SupplierOptionDto[]>([]);
  const [factories, setFactories] = useState<FactoryOptionDto[]>([]);
  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [documentDownloading, setDocumentDownloading] = useState(false);
  const [chooseSupplierOpen, setChooseSupplierOpen] = useState(false);
  const [sendingToSupplier, setSendingToSupplier] = useState(false);
  // Stage „Szkic” of the ZD card (karta ZD według makiety): „Pozycje i ceny”,
  // „Odrzuć” and „Więcej danych”.
  const [linesOpen, setLinesOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [moreDataSaving, setMoreDataSaving] = useState(false);
  // Stages 2–5 (karta ZD według makiety): trucks come from the „Dostawa” card,
  // PZ and purchase invoices from the stage data hook.
  const [stageTransports, setStageTransports] = useState<SupplierOrderTransportDto[] | null | undefined>(undefined);
  const [supplierConfirmOpen, setSupplierConfirmOpen] = useState(false);
  const [supplierConfirmDate, setSupplierConfirmDate] = useState<Dayjs | null>(null);
  const [stageSaving, setStageSaving] = useState(false);
  const [stageInvoiceId, setStageInvoiceId] = useState<string | null>(null);
  const [invoiceDelivery, setInvoiceDelivery] = useState<{ transportId?: string; receiptOnly?: boolean; transportLabel?: string; truckOpenQuantities?: Record<string, number> }>({});
  const [historyOpen, setHistoryOpen] = useState(false);
  // The trucks belong to one ZD: another order id starts in the loading state.
  useEffect(() => { setStageTransports(undefined); }, [id]);
  const handleTransportsLoaded = useCallback((transports: SupplierOrderTransportDto[] | null | undefined) => setStageTransports(transports), []);
  const stageData = useSupplierOrderStageData({
    supplierOrderId: id,
    canReadInvoices: authService.canManagePurchaseInvoiceImports(),
    revision: supplierOrder,
  });

  const inDeliveryWorkspace = !!supplierOrder && ['SHIPPED', 'PARTIALLY_RECEIVED', 'RECEIVED', 'INVOICED'].includes(supplierOrder.status) && authService.canManageSupplierOrders();
  const canManage = authService.canManageSupplierOrders();
  const canViewSupplierPurchasePrices = authService.canViewSupplierOrderPurchasePrices();
  const canDownloadSupplierOrderPdf = authService.canViewSupplierOrderPurchasePdf();
  const canEditSupplierPurchasePrices = authService.canEditSupplierOrderPurchasePrices();
  const canEditSupplierOrderFinance = authService.canEditSupplierOrderFinance();

  // Fetches the supplier-order PDF as a blob and triggers a browser download,
  // in the language picked in the „Więcej” menu.
  const handleDownloadDocument = async (documentLanguage: 'en' | 'pl') => {
    if (!id || !supplierOrder || !canDownloadSupplierOrderPdf) return;

    const conversionChoice = await promptOrderPdfConversionUnit(modal, t);

    if (!conversionChoice) return;

    setDocumentDownloading(true);
    try {
      const orderBlob = await downloadSupplierOrderPdf(
        id,
        documentLanguage,
        conversionChoice === 'none' ? undefined : conversionChoice,
      );
      const url = window.URL.createObjectURL(new Blob([orderBlob]));
      const link = document.createElement('a');
      const filePrefix = documentLanguage === 'pl' ? 'zamowienie' : 'order';
      link.href = url;
      link.setAttribute('download', `${buildSafePdfFileName(filePrefix, displayOrderNumber)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      feedback.success(t('orders:orderDetail.orderDocumentDownloaded', { orderNumber: displayOrderNumber }));
    } catch (error) {
      feedback.error(t('orders:orderDetail.orderDocumentDownloadFailed', { orderNumber: displayOrderNumber }));
    } finally {
      setDocumentDownloading(false);
    }
  };

  // Supplier and factory options feed the „Wybierz dostawcę” dialog and „Więcej danych”.
  useEffect(() => {
    if (!canManage) return;

    (async () => {
      try {
        const [supplierRows, factoryRows] = await Promise.all([
          fetchSupplierOptions(),
          fetchFactoryOptions(),
        ]);
        setSuppliers(supplierRows);
        setFactories(factoryRows);
      } catch {
        // Non-critical — selects fall back to the current value.
      }
    })();
  }, [canManage]);

  const startEditing = (field: SupplierOrderEditableField) => {
    if (!supplierOrder) return;

    editForm.resetFields();
    setEditingField(field);
    editForm.setFieldsValue(getSupplierOrderInitialValues(supplierOrder, field));
  };

  // "Przejrzyj szkic" (?review=1): a confirmation draft opens with the
  // supplier choice open over the card (usability round 2026-10-05: not an
  // inline editor in a collapsed section) — the system never guesses it.
  const [searchParams] = useSearchParams();
  const reviewStarted = useRef(false);
  useEffect(() => {
    if (reviewStarted.current || !canManage || searchParams.get('review') !== '1') return;
    if (!supplierOrder || supplierOrder.id !== id || supplierOrder.draftReviewStatus !== 'PENDING') return;
    reviewStarted.current = true;
    if (!supplierOrder.supplierId) setChooseSupplierOpen(true);
  }, [supplierOrder, id, canManage, searchParams]);

  // „Wyślij do dostawcy” on the draft banner: the status change to „Wysłane”
  // with the backend's supplier check; without a supplier the choice opens.
  const handleSendToSupplier = async () => {
    if (!id || !supplierOrder) return;
    if (!supplierOrder.supplierId) {
      feedback.warning(t('orders:supplierOrders.drafts.errors.supplierRequired'));
      setChooseSupplierOpen(true);
      return;
    }
    const unpriced = (supplierOrder.items || []).filter((item) => !(Number(item.unitCost || 0) > 0)).length;
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('orders:supplierOrders.drafts.sendConfirmTitle', {
          number: displayOrderNumber,
          supplier: supplierOrder.supplier?.name || '',
        }),
        content: (
          <>
            <div>{t('orders:supplierOrders.drafts.sendConfirmContent')}</div>
            {canViewSupplierPurchasePrices && unpriced > 0 ? (
              <div style={{ marginTop: 8 }}>{t('orders:supplierOrders.drafts.sendUnpricedWarning', { count: unpriced })}</div>
            ) : null}
          </>
        ),
        okText: t('orders:supplierOrders.drafts.sendToSupplier'),
        cancelText: t('common:cancel'),
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;
    setSendingToSupplier(true);
    try {
      const saved = await updateSupplierOrder(
        id,
        buildSupplierOrderUpdateData(supplierOrder, 'status', { status: 'SENT' }),
        { modal, t },
      );
      if (!saved) return;
      feedback.success(t('orders:supplierOrders.drafts.sendSuccess', {
        number: displayOrderNumber,
        supplier: supplierOrder.supplier?.name || '',
      }));
      await refresh();
    } catch (error: any) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.statusUpdateError', {
        orderNumber: displayOrderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
    } finally {
      setSendingToSupplier(false);
    }
  };

  // Stages 2–5: the status moves by the work of the card (one request, one toast).
  const moveToStatus = async (status: SupplierOrderStatus, extra?: Record<string, unknown>) => {
    if (!id || !supplierOrder) return false;
    setStageSaving(true);
    try {
      const saved = await updateSupplierOrder(
        id,
        { ...buildSupplierOrderUpdateData(supplierOrder, 'status', { status }), ...(extra || {}) },
        { modal, t },
      );
      if (!saved) return false;
      feedback.success(t('orders:supplierOrders.detail.statusUpdateSuccess', {
        orderNumber: displayOrderNumber,
        status: t(`orders:supplierOrders.status.${status}`),
      }));
      const detachFailed = transportDetachFailedWarning(saved, t);
      if (detachFailed) feedback.warning(detachFailed, { sticky: true });
      await refresh();
      return true;
    } catch (error: any) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.statusUpdateError', {
        orderNumber: displayOrderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
      return false;
    } finally {
      setStageSaving(false);
    }
  };

  const saveSupplierConfirmation = async () => {
    if (!supplierConfirmDate) return;
    const readyDate = supplierConfirmDate.format('YYYY-MM-DD');
    // The ZK schedule reads the ready date as `estimatedCompletionDate` („gotowe w fabryce”), the card tile as `expectedDelivery`.
    const saved = await moveToStatus('CONFIRMED', { expectedDelivery: readyDate, estimatedCompletionDate: readyDate });
    if (saved) setSupplierConfirmOpen(false);
  };

  // Click on a finished stage of the rail: back to it, after a short confirmation (nothing is undone but the status).
  const handleReturnToStage = (stageIndex: number) => {
    const target = STAGE_RETURN_STATUS[stageIndex];
    if (!target || !allowedStatusTransitions.includes(target)) return;
    modal.confirm({
      title: t('orders:supplierOrders.card.returnTitle', { stage: t(`orders:supplierOrders.card.stages.${SUPPLIER_ORDER_STAGES[stageIndex].key}`) }),
      content: t('orders:supplierOrders.card.returnContent', { status: t(`orders:supplierOrders.status.${target}`) }),
      okText: t('orders:supplierOrders.card.returnOk'),
      cancelText: t('common:cancel'),
      onOk: () => moveToStatus(target),
    });
  };

  const handleStageAction = (action: SupplierOrderStageActionKey) => {
    if (!id) return;
    if (action === 'confirmBySupplier') {
      setSupplierConfirmDate(supplierOrder?.expectedDelivery ? dayjs(supplierOrder.expectedDelivery) : dayjs());
      setSupplierConfirmOpen(true);
    } else if (action === 'planTransport') {
      navigate(withTransportReturnContext(
        `/app/transports/new?supplierOrderId=${encodeURIComponent(id)}&kind=FACTORY_TO_WAREHOUSE`,
        { returnTo: `/app/supplier-orders/${encodeURIComponent(id)}` },
      ));
    } else {
      setStageInvoiceId('new');
    }
  };

  const cancelEditing = (field: SupplierOrderEditableField) => {
    if (editingField !== field) return;

    setEditingField(null);
    editForm.resetFields();
  };

  const saveField = async (field: SupplierOrderEditableField) => {
    if (!id || !supplierOrder) return;
    if (field === 'currency' && !canEditSupplierPurchasePrices) return;
    if (field === 'landedCosts' && !canEditSupplierOrderFinance) return;

    try {
      const values = editForm.getFieldsValue(true);
      // recursive: true descends into Form.List entries (landedCosts items);
      // a bare ['landedCosts'] path would skip the per-item amount rules.
      await editForm.validateFields(getSupplierOrderFieldNames(field, values), { recursive: true });
      setSavingField(field);
      const updateData = buildSupplierOrderUpdateData(supplierOrder, field, values);

      const saved = await updateSupplierOrder(id, updateData, { modal, t });
      if (!saved) return;
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', { orderNumber: displayOrderNumber }));
      const valuationIssues = readIncompleteValuationIssues(saved);
      if (valuationIssues) feedback.warning(incompleteValuationWarning(valuationIssues, t), { sticky: true });
      const staleSplit = staleTransportSplitWarning(saved, t);
      if (staleSplit) feedback.warning(staleSplit, { sticky: true });
      cancelEditing(field);
      await refresh();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
        orderNumber: displayOrderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
    } finally {
      setSavingField(null);
    }
  };

  const saveOrderNumber = async () => {
    if (!id || !supplierOrder) return;
    try {
      const values = numberForm.getFieldsValue(true);
      await numberForm.validateFields(getSupplierOrderFieldNames('manualNumbering', values), { recursive: true });
      setSavingField('manualNumbering');
      const updateData = buildSupplierOrderUpdateData(supplierOrder, 'manualNumbering', values);
      await updateSupplierOrder(id, updateData);
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', {
        orderNumber: getDisplayOrderNumberAfterUpdate(supplierOrder, updateData),
      }));
      setNumberModalOpen(false);
      await refresh();
    } catch (error: any) {
      if (error?.errorFields) return;
      const taken = getSupplierOrderNumberTaken(error);
      if (taken) {
        setNumberTaken(taken);
        return;
      }
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
        orderNumber: displayOrderNumber,
      }));
    } finally {
      setSavingField(null);
    }
  };

  // „Więcej danych” on stage „Szkic”: the former wizard fields in one PUT.
  const saveMoreData = async (values: SupplierOrderMoreDataValues): Promise<boolean> => {
    if (!id || !supplierOrder) return false;
    setMoreDataSaving(true);
    try {
      const updateData = buildSupplierOrderMoreDataUpdateData(supplierOrder, values);
      const saved = await updateSupplierOrder(id, updateData, { modal, t });
      if (!saved) return false;
      feedback.success(t('orders:supplierOrders.detail.updateSuccess', {
        orderNumber: getDisplayOrderNumberAfterUpdate(supplierOrder, updateData),
      }));
      await refresh();
      return true;
    } catch (error: any) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.updateError', {
        orderNumber: displayOrderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
      return false;
    } finally {
      setMoreDataSaving(false);
    }
  };

  const getNumberModalInitialValues = (): Record<string, unknown> => {
    if (!supplierOrder) return {};
    const init = getSupplierOrderInitialValues(supplierOrder, 'manualNumbering');
    if (!supplierOrder.useManualNumbering) {
      init.manualOrderNumber = getSupplierOrderDisplayNumber(supplierOrder);
    }
    return init;
  };

  const openStatusModal = () => {
    if (!supplierOrder) return;
    const [firstAllowedStatus] = supplierOrder.allowedTransitions || [];
    statusForm.setFieldsValue({ status: firstAllowedStatus, statusNotes: '' });
    setStatusModalVisible(true);
  };

  const handleChangeStatus = async (values: any) => {
    if (!id || !supplierOrder) return;

    setStatusSaving(true);
    try {
      // A cancel of a ZD still on trucks asks first (it is taken off them).
      const saved = await updateSupplierOrder(
        id,
        {
          ...buildSupplierOrderUpdateData(supplierOrder, 'status', { status: values.status }),
          statusNotes: values.statusNotes?.trim() || null,
        },
        { modal, t },
      );
      if (!saved) return;
      feedback.success(t('orders:supplierOrders.detail.statusUpdateSuccess', {
        orderNumber: displayOrderNumber,
        status: t(`orders:supplierOrders.status.${values.status}`),
      }));
      const detachFailed = transportDetachFailedWarning(saved, t);
      if (detachFailed) feedback.warning(detachFailed, { sticky: true });
      setStatusModalVisible(false);
      statusForm.resetFields();
      await refresh();
    } catch (error: any) {
      feedback.error(getSupplierOrderErrorMessage(error, t, 'orders:supplierOrders.detail.statusUpdateError', {
        orderNumber: displayOrderNumber,
      }), { sticky: isBlockingSupplierOrderError(error) });
    } finally {
      setStatusSaving(false);
    }
  };

  const statusHistoryEntries = useMemo<SupplierOrderStatusHistoryDto[]>(() => {
    if (!supplierOrder) return [];
    return Array.isArray(supplierOrder.statusHistory) && supplierOrder.statusHistory.length > 0
      ? supplierOrder.statusHistory
      : [{
        id: 'current',
        supplierOrderId: supplierOrder.id,
        status: supplierOrder.status,
        changedAt: supplierOrder.orderDate,
        notes: undefined,
      }];
  }, [supplierOrder]);

  // „Historia” also lists the ZD valuation revisions (review of PR #418 on
  // staging), loaded on open; the read needs purchase-price access
  // (backend requireStockValuationRead). Without it: statuses only.
  const canReadValuationHistory = canManage && canViewSupplierPurchasePrices;
  const [valuationHistory, setValuationHistory] = useState<StockValuation['history'] | null>(null);
  const [valuationHistoryFailed, setValuationHistoryFailed] = useState(false);
  useEffect(() => {
    if (!historyOpen || !canReadValuationHistory || !supplierOrder?.id) return undefined;
    let active = true;
    setValuationHistory(null);
    setValuationHistoryFailed(false);
    fetchStockValuation(supplierOrder.id)
      .then((valuation) => { if (active) setValuationHistory(valuation.history || []); })
      .catch(() => { if (active) setValuationHistoryFailed(true); });
    return () => { active = false; };
  }, [historyOpen, canReadValuationHistory, supplierOrder?.id]);
  const historyItems = useMemo(
    () => mergeSupplierOrderHistory(statusHistoryEntries, valuationHistory),
    [statusHistoryEntries, valuationHistory],
  );

  const landedCostCategoryOptions = useMemo(
    () => SUPPLIER_ORDER_MANUAL_LANDED_COST_CATEGORIES.map((cat) => ({
      value: cat,
      label: t(`orders:supplierOrders.landedCostCategory.${cat}`),
    })),
    [t]
  );

  if (loading && (!supplierOrder || supplierOrder.id !== id)) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!supplierOrder) {
    return (
      <div style={{ paddingBottom: 24 }}>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => goBack(SUPPLIER_ORDERS_LIST_PATH)}
          style={{ marginBottom: 16, borderRadius: 999, fontWeight: 600 }}
        >
          {t('orders:supplierOrders.detail.backToList')}
        </Button>
        <Card style={SECTION_CARD_STYLE} bodyStyle={SECTION_CARD_BODY_STYLE}>
          {t('orders:supplierOrders.detail.notFound')}
        </Card>
      </div>
    );
  }

  const isFinancialCompletenessPending = supplierOrder.financialCompleteness?.status === 'PENDING';
  const supplierOrderLineCurrencies = Array.from(new Set(
    (supplierOrder.items || [])
      .map((item) => item.currency || supplierOrder.currency || 'PLN')
      .filter(Boolean)
  )).sort();
  const supplierOrderLineCurrenciesLabel = supplierOrderLineCurrencies.length > 0
    ? supplierOrderLineCurrencies.join(', ')
    : '-';
  const allowedStatusTransitions = supplierOrder.allowedTransitions || [];
  const currentProgressStatus = supplierOrder.status as SupplierOrderStatus;
  const supplierStatusTone: 'default' | 'done' | 'warning' = ['RECEIVED', 'INVOICED'].includes(supplierOrder.status)
    ? 'done'
    : ['ON_HOLD', 'CANCELLED'].includes(supplierOrder.status) ? 'warning' : 'default';
  const isSupplierWorkflowCancelled = supplierOrder.status === 'CANCELLED';
  const isSupplierWorkflowHeld = supplierOrder.status === 'ON_HOLD';
  // A rejected confirmation draft keeps status „Szkic” but is done with: it
  // shows the rejection banner and the plain rail, not the stage tasks.
  const inDraftStage = supplierOrder.status === 'DRAFT' && supplierOrder.draftReviewStatus !== 'REJECTED';
  // Stages 2–5 (karta ZD według makiety); Wstrzymane / Anulowane / rejected drafts keep the plain rail.
  const inStagePanel = !inDraftStage && supplierOrder.draftReviewStatus !== 'REJECTED' && getSupplierOrderStageIndex(supplierOrder.status) >= 1;
  const canViewTransportCosts = authService.canViewTransportCosts();
  const invoiceComplete = supplierOrder.status === 'INVOICED' || supplierOrder.invoiceCoverage?.state === 'FULL';
  const stageModel = buildSupplierOrderStageModel({
    status: supplierOrder.status,
    expectedDelivery: supplierOrder.expectedDelivery ? formatDateEuropean(supplierOrder.expectedDelivery) : null,
    transportCount: stageTransports === null ? null : stageTransports?.length,
    freightUnknown: canViewTransportCosts && Boolean(stageTransports?.some((transport) => (
      transport.kind !== 'WAREHOUSE_TO_CUSTOMER' && (!transport.freight || transport.freight.amountPLN === null)
    ))),
    pzNumbers: stageData.pzNumbers,
    invoiceNumbers: stageData.invoiceNumbers,
    invoiceComplete,
  });
  const supplierWorkflowSteps = SUPPLIER_ORDER_PROGRESS_STATUSES.map((status) => ({
    key: status,
    label: t(`orders:supplierOrders.status.${status}`),
  }));
  const supplierWorkflowCurrentLabel = t(
    isSupplierWorkflowCancelled
      ? 'orders:supplierOrders.detail.cancelledWorkflow'
      : isSupplierWorkflowHeld
        ? 'orders:supplierOrders.detail.heldWorkflow'
        : 'orders:supplierOrders.detail.currentStatus'
  );

  const landedCostsForm = () => (
    <Form form={editForm} layout="vertical" style={{ width: '100%' }}>
      <Form.List name="landedCosts">
        {(fields, { add, remove }) => (
          <div style={{ display: 'grid', gap: 8 }}>
            {fields.map((field) => (
              <Row key={field.key} gutter={[8, 0]} align="top" style={{ flexWrap: 'nowrap' }}>
                <Col flex="180px">
                  <Form.Item
                    name={[field.name, 'category']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostCategory') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true }]}
                  >
                    <Select options={landedCostCategoryOptions} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col flex="120px">
                  <Form.Item
                    name={[field.name, 'amount']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostAmount') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ ...supplierOrderAmountRule(t), required: true }]}
                  >
                    {/* No min/max clamp: an out-of-range entry must stay visible and
                        fail validation instead of being silently coerced on blur. */}
                    <InputNumber
                      precision={2}
                      style={{ width: '100%' }}
                      {...POLISH_DECIMAL_INPUT_NUMBER_PROPS}
                    />
                  </Form.Item>
                </Col>
                <Col flex="96px">
                  <Form.Item
                    name={[field.name, 'currency']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostCurrency') : undefined}
                    style={{ marginBottom: 8 }}
                    rules={[{ required: true }]}
                  >
                    <Select
                      style={{ width: '100%' }}
                      options={ORDER_CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
                    />
                  </Form.Item>
                </Col>
                <Col flex="auto">
                  <Form.Item
                    name={[field.name, 'note']}
                    label={fields.indexOf(field) === 0 ? t('orders:supplierOrders.form.landedCostNote') : undefined}
                    style={{ marginBottom: 8 }}
                  >
                    <Input maxLength={500} placeholder={t('orders:supplierOrders.form.landedCostNote')} />
                  </Form.Item>
                </Col>
                <Col flex="32px">
                  <Form.Item
                    label={fields.indexOf(field) === 0 ? ' ' : undefined}
                    style={{ marginBottom: 8 }}
                  >
                    <Button
                      type="text"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </Form.Item>
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              onClick={() => add({ category: 'CUSTOMS_DUTY', amount: 0, currency: supplierOrder.currency || 'PLN', note: '' })}
              icon={<PlusOutlined />}
              style={{ width: '100%' }}
            >
              {t('orders:supplierOrders.form.addLandedCost')}
            </Button>
          </div>
        )}
      </Form.List>
    </Form>
  );

  // Summary currency; when the lines are priced in other currencies they follow in the same tile.
  const currencyTileValue = canViewSupplierPurchasePrices && supplierOrder.currency
    ? (supplierOrderLineCurrencies.length > 0 && supplierOrderLineCurrenciesLabel !== supplierOrder.currency
      ? `${supplierOrder.currency} · ${t('orders:supplierOrders.detail.purchaseLineCurrenciesShort', { currencies: supplierOrderLineCurrenciesLabel })}`
      : supplierOrder.currency)
    : null;

  // The supplier's confirmation (status „Potwierdzone” or later) makes the ready date firm.
  const readyConfirmed = !['DRAFT', 'SENT'].includes(supplierOrder.status);

  const overviewMeta = [
    {
      icon: <ShopOutlined />,
      label: t('orders:supplierOrders.form.supplier'),
      value: supplierOrder.supplier?.name || '-',
    },
    {
      icon: <FileTextOutlined />,
      label: t('orders:supplierOrders.card.header.source'),
      value: supplierOrder.draftSourceOrderId
        ? t('orders:supplierOrders.card.header.sourceDraft')
        : t('orders:supplierOrders.card.header.sourceNew'),
    },
    // The factory tile only when there is one: an empty „-” tile would only break the even row.
    ...(supplierOrder.factory?.name ? [{
      icon: <HomeOutlined />,
      label: t('orders:supplierOrders.form.factory'),
      value: supplierOrder.factory.name,
    }] : []),
    {
      icon: <CalendarOutlined />,
      label: t('orders:supplierOrders.form.orderDate'),
      value: supplierOrder.orderDate ? formatDateEuropean(supplierOrder.orderDate) : '-',
    },
    {
      icon: <TruckOutlined />,
      label: t('orders:supplierOrders.card.header.ready'),
      value: supplierOrder.expectedDelivery
        ? t(readyConfirmed ? 'orders:supplierOrders.card.header.readyConfirmed' : 'orders:supplierOrders.card.header.readyPlanned', { date: formatDateEuropean(supplierOrder.expectedDelivery) })
        : '-',
    },
    ...(currencyTileValue ? [{
      icon: <DollarOutlined />,
      label: t('orders:process.chips.currency'),
      value: currencyTileValue,
    }] : []),
  ];

  // „Więcej” of the header (as on the ZK card): PDF in both languages, history, number.
  const headerMenuItems: MenuProps['items'] = [
    ...(canDownloadSupplierOrderPdf ? [
      { key: 'pdf-pl', icon: <FilePdfOutlined />, label: t('orders:supplierOrders.card.menu.pdfPl') },
      { key: 'pdf-en', icon: <FilePdfOutlined />, label: t('orders:supplierOrders.card.menu.pdfEn') },
    ] : []),
    // With the valuation history the number of entries is known only after
    // opening (loaded on demand), so the label has no count then.
    { key: 'history', icon: <FileTextOutlined />, label: canReadValuationHistory
      ? t('orders:supplierOrders.card.menu.historyWithValuation')
      : t('orders:supplierOrders.card.menu.history', { count: statusHistoryEntries.length }) },
    ...(canManage ? [{ key: 'number', icon: <EditOutlined />, label: t('orders:orderDetail.editNumber') }] : []),
  ];
  const handleHeaderMenu = (key: string) => {
    if (key === 'pdf-pl') void handleDownloadDocument('pl');
    else if (key === 'pdf-en') void handleDownloadDocument('en');
    else if (key === 'history') setHistoryOpen(true);
    else if (key === 'number') {
      setNumberTaken(null);
      setNumberModalOpen(true);
    }
  };

  return (
    <div className={`zk-page${inDeliveryWorkspace ? ' zd-delivery-page' : ''}`}>
      <div style={inDeliveryWorkspace ? undefined : PAGE_SHELL_STYLE} className="order-detail-page zk-process">
        {/* Header by the same rules as the ZK card (process/DESIGN.md): one low card, equal tiles, small buttons. */}
        <section className="zk-section zk-head zk-head--zd" data-testid="supplier-order-header">
          <div className="zk-head__main">
            <div className="zk-head__tags">
              <Button icon={<ArrowLeftOutlined />} onClick={() => goBack(SUPPLIER_ORDERS_LIST_PATH)}>
                {t('orders:process.back')}
              </Button>
              <span className="zk-tag">
                {t('orders:supplierOrders.detail.title')} <span className="zk-head__number">{displayOrderNumber}</span>
              </span>
              <span className={`zk-pill${supplierStatusTone === 'default' ? '' : ` zk-pill--${supplierStatusTone}`}`} data-testid="supplier-order-status-pill">
                {t(`orders:supplierOrders.status.${supplierOrder.status}`)}
              </span>
              {isFinancialCompletenessPending && (
                <Tag color="gold" style={{ marginInlineEnd: 0 }}>
                  {t('orders:supplierOrders.detail.financialCompletenessPending')}
                </Tag>
              )}
              {supplierOrder.commerciallyFrozenAt && (
                <Tooltip title={t('orders:supplierOrders.detail.frozenOrderAlert')}>
                  {/* WP11: „Zafakturowane częściowo (x %)” / „w całości”; the plain tag only when coverage is unknown. */}
                  {supplierOrder.invoiceCoverage && supplierOrder.invoiceCoverage.state !== 'NONE'
                    ? <span><InvoiceCoverageTag coverage={supplierOrder.invoiceCoverage} /></span>
                    : <Tag style={{ marginInlineEnd: 0 }}>{t('invoices:workflow.frozenOrder')}</Tag>}
                </Tooltip>
              )}
              {supplierOrder.useManualNumbering && (
                <Tag color="blue" style={{ marginInlineEnd: 0 }}>{t('orders:orderDetail.manual')}</Tag>
              )}
              <Modal
                open={numberModalOpen}
                title={t('orders:orderDetail.editNumber')}
                onCancel={() => setNumberModalOpen(false)}
                onOk={saveOrderNumber}
                okText={t('common:save')}
                cancelText={t('common:cancel')}
                confirmLoading={savingField === 'manualNumbering'}
                destroyOnClose
              >
                <Form form={numberForm} layout="vertical" initialValues={getNumberModalInitialValues()}
                  onValuesChange={() => setNumberTaken(null)}>
                  <Form.Item
                    name="useManualNumbering"
                    valuePropName="checked"
                    label={t('orders:orderDetail.useManualNumbering')}
                  >
                    <Switch />
                  </Form.Item>
                  {numberFormUseManual && (
                    <Form.Item
                      name="manualOrderNumber"
                      label={t('orders:supplierOrders.form.manualOrderNumber')}
                      validateStatus={numberTaken ? 'error' : undefined}
                      help={numberTaken ? <SupplierOrderNumberTakenHelp taken={numberTaken} /> : undefined}
                      rules={[{
                        required: true,
                        whitespace: true,
                        message: t('orders:modal.validation.manualNumberRequired'),
                      }, supplierOrderNumberRule(t)]}
                    >
                      <Input maxLength={64} placeholder={t('orders:orderDetail.manualOrderNumberPlaceholder')} />
                    </Form.Item>
                  )}
                </Form>
              </Modal>
            </div>
          </div>
          <div className="zk-head__actions">
            {canManage && (
              <Button type="primary" onClick={openStatusModal}>
                {t('orders:orderDetail.updateStatus')}
              </Button>
            )}
            <Dropdown menu={{ items: headerMenuItems, onClick: ({ key }) => handleHeaderMenu(String(key)) }} trigger={['click']}>
              <Button icon={<MoreOutlined />} loading={documentDownloading} data-testid="supplier-order-more-actions">
                {t('orders:process.more')}
              </Button>
            </Dropdown>
          </div>
          <div className="zk-chips zk-chips--zd" style={{ '--zd-cols': overviewMeta.length } as React.CSSProperties}>
            {overviewMeta.map((item) => (
              <div className="zk-chip" key={item.label}>
                <span className="zk-chip__label">{item.label}</span>
                <span className="zk-chip__value">
                  {item.icon}
                  <span className="zk-chip__text" title={typeof item.value === 'string' ? item.value : undefined}>{item.value}</span>
                </span>
              </div>
            ))}
          </div>
          {/* Only with a loaded list: before it (or without the „Dostawa” card) „brak” would be a claim, not a fact. */}
          {stageTransports && !inDeliveryWorkspace ? (
            <SupplierOrderTransportStrip
              transports={stageTransports}
              onOpenTransport={(transportId) => navigate(`/app/transports/${transportId}`)}
            />
          ) : null}
          <OrderDocumentCompletenessSection visible={authService.canViewOrderDocumentCompleteness()} value={supplierOrder.documentCompleteness} status={supplierOrder.status}
            onPlanTransport={canManageTransportRoutes(authService.getCurrentUser())
              && (inDeliveryWorkspace || inStagePanel)
              && canPlanSupplierOrderTransport(supplierOrder.status, { inDeliveryWorkspace })
              ? () => handleStageAction('planTransport') : undefined} />
          <div className="zk-head__alerts">
            <SupplierOrderDraftReviewBanner supplierOrder={supplierOrder} />
            {supplierOrder.archivedAt && (
              // WP12 B1 "Anuluj i ukryj": the card stays readable by its link.
              <Alert
                type="warning"
                showIcon
                message={t('orders:supplierOrders.archive.banner', { date: formatDateEuropean(supplierOrder.archivedAt) })}
                description={authService.getCurrentUser()?.role === 'ADMIN'
                  ? t('orders:supplierOrders.archive.bannerAdmin')
                  : t('orders:supplierOrders.archive.bannerOthers')}
                action={authService.getCurrentUser()?.role === 'ADMIN' ? (
                  <Button
                    size="small"
                    onClick={async () => {
                      const orderNumber = getSupplierOrderDisplayNumber(supplierOrder);
                      try {
                        await unarchiveSupplierOrder(supplierOrder.id);
                        feedback.success(t('orders:supplierOrders.archive.unarchiveSuccess', { orderNumber }));
                        await refresh();
                      } catch {
                        feedback.error(t('orders:supplierOrders.archive.unarchiveError', { orderNumber }));
                      }
                    }}
                  >
                    {t('orders:supplierOrders.archive.unarchive')}
                  </Button>
                ) : undefined}
              />
            )}
            {!!supplierOrder.withdrawnInvoiceReceipts?.length && (
              <Alert type="warning" showIcon data-testid="supplier-order-withdrawn-invoice"                 message={t('orders:supplierOrders.detail.withdrawnInvoiceReceipts', { numbers: supplierOrder.withdrawnInvoiceReceipts.join(', ') })} />
            )}
            {supplierOrder.commerciallyFrozenAt && (
              // WP1 part 2: an invoiced ZD stays editable; say once what a
              // change after the invoice means (confirmation, money stays).
              <Alert
                type="info"
                showIcon
                message={inDeliveryWorkspace ? <Tooltip title={t('orders:supplierOrders.detail.frozenOrderAlert')}>{t('orders:supplierOrders.deliveries.partialHint')}</Tooltip> : t('orders:supplierOrders.detail.frozenOrderAlert')}
                              />
            )}
          </div>
        </section>
            {canManage ? (
              <SupplierOrderChooseSupplierModal
                open={chooseSupplierOpen}
                supplierOrder={supplierOrder}
                suppliers={suppliers}
                canEditPurchasePrices={canEditSupplierPurchasePrices}
                onClose={() => setChooseSupplierOpen(false)}
                onSaved={refresh}
                onSupplierCreated={(supplier) => setSuppliers((current) => [...current, supplier])}
              />
            ) : null}
            {canManage && supplierOrder.draftReviewStatus === 'PENDING' ? (
              <SupplierOrderDraftRejectModal
                open={rejectOpen}
                supplierOrderId={supplierOrder.id}
                onClose={() => setRejectOpen(false)}
                onRejected={() => navigate(`${SUPPLIER_ORDERS_LIST_PATH}&supplierView=drafts`)}
              />
            ) : null}
        {inDraftStage ? (
          // Karta ZD według makiety: stage 1 „Szkic” — the same panel as a
          // new ZD (/app/supplier-orders/new) and a confirmation draft.
          <SupplierOrderDraftStagePanel
            isNew={false}
            supplierName={supplierOrder.supplier?.name || null}
            currency={canViewSupplierPurchasePrices ? supplierOrder.currency : null}
            lines={supplierOrder.items || []}
            pricesVisible={canViewSupplierPurchasePrices}
            sourceOrderId={supplierOrder.draftSourceOrderId}
            expectedDelivery={supplierOrder.expectedDelivery}
            canManage={canManage}
            canReject={supplierOrder.draftReviewStatus === 'PENDING'}
            canSendStatus={allowedStatusTransitions.includes('SENT')}
            sending={sendingToSupplier}
            onChooseSupplier={() => setChooseSupplierOpen(true)}
            onOpenLines={() => setLinesOpen(true)}
            onSend={() => { void handleSendToSupplier(); }}
            onReject={() => setRejectOpen(true)}
          >
            <SupplierOrderMoreDataSection
              values={getSupplierOrderMoreDataInitialValues(supplierOrder)}
              factories={factories}
              canEdit={canManage}
              onSave={saveMoreData}
              saving={moreDataSaving}
              onFactoryCreated={(factory) => setFactories((current) => [...current, factory])}
            />
          </SupplierOrderDraftStagePanel>
        ) : inDeliveryWorkspace ? (
          <>
            <SupplierOrderStageRail currentIndex={getSupplierOrderStageIndex(supplierOrder.status)}
              subStatusLabel={t(`orders:supplierOrders.status.${supplierOrder.status}`)}
              onReturnToStage={canManage && supplierOrder.status !== 'INVOICED' ? handleReturnToStage : undefined} />
            <SupplierOrderDeliveries
              supplierOrderId={supplierOrder.id}
              revision={supplierOrder}
              items={supplierOrder.items || []}
              onOpenInvoice={(id) => { setInvoiceDelivery({}); setStageInvoiceId(id); }}
              onDeliveryInvoice={(transportId, id, receiptOnly, transportLabel, truckOpenQuantities) => { setInvoiceDelivery({ transportId, receiptOnly, transportLabel, truckOpenQuantities }); setStageInvoiceId(id); }}
              actions={<>
                <SupplierOrderDeliveryTransportsSection
                  supplierOrderId={supplierOrder.id}
                  supplierOrderLabel={displayOrderNumber}
                  loadingPlace={resolveSupplierOrderLoadingAddress(supplierOrder)}
                  supplierOrderStatus={supplierOrder.status}
                  canPlanTransports={canManageTransportRoutes(authService.getCurrentUser())}
                  fullyOnTrucks={supplierOrder.truckCoverage === 'FULL'}
                  onOpenTransport={(transportId) => navigate(`/app/transports/${transportId}`)}
                  onLoaded={handleTransportsLoaded}
                  onChanged={() => { void refresh(); }}
                  hideList
                />
                {authService.canManagePurchaseInvoiceImports() ? <Button onClick={() => { setInvoiceDelivery({}); setStageInvoiceId('new'); }}>
                  {t('orders:supplierOrders.deliveries.invoiceWithoutDelivery')}
                </Button> : null}
              </>}
            />
            <SupplierOrderMoreDataSection
              values={getSupplierOrderMoreDataInitialValues(supplierOrder)}
              factories={factories}
              canEdit={canManage}
              onSave={saveMoreData}
              saving={moreDataSaving}
              onFactoryCreated={(factory) => setFactories((current) => [...current, factory])}
            />
          </>
        ) : inStagePanel ? (
          // Karta ZD według makiety, etapy 2–5: the same panel shape as „Szkic”,
          // with the side card „Dostawa” (trucks incl. straight to the customer).
          <SupplierOrderStagePanel
            status={supplierOrder.status}
            model={stageModel}
            canManage={canManage}
            allowedTransitions={allowedStatusTransitions}
            transportCount={stageTransports ? stageTransports.length : null}
            unavailableActions={[
              ...(authService.canManagePurchaseInvoiceImports() ? [] : ['addInvoice' as const]),
              ...(canManageTransportRoutes(authService.getCurrentUser()) ? [] : ['planTransport' as const]),
            ]}
            saving={stageSaving}
            onAction={handleStageAction}
            onNextStatus={(status) => { void moveToStatus(status); }}
            onReturnToStage={handleReturnToStage}
            aside={(
              <SupplierOrderDeliveryAside
                supplierOrderId={supplierOrder.id}
                supplierOrderLabel={getSupplierOrderSummary(supplierOrder).displayNumber}
                loadingPlace={resolveSupplierOrderLoadingAddress(supplierOrder)}
                // Planning a truck is for goods still to come: not once the goods are received.
                canPlanTransports={canManageTransportRoutes(authService.getCurrentUser())
                  && canPlanSupplierOrderTransport(supplierOrder.status, { inDeliveryWorkspace: false })}
                canViewCosts={canViewTransportCosts}
                fullyOnTrucks={supplierOrder.truckCoverage === 'FULL'}
                onOpenTransport={(transportId) => navigate(`/app/transports/${transportId}`)}
                onTransportsLoaded={handleTransportsLoaded}
                onTransportsChanged={() => { void refresh().catch(() => undefined); }}
                transports={stageTransports}
                freightPLN={canViewSupplierPurchasePrices ? buildSupplierOrderCostSummary(supplierOrder, stageTransports ?? null).freightPLN : undefined}
                pzNumbers={stageData.pzNumbers}
                invoiceNumbers={stageData.invoiceNumbers}
                invoices={stageData.invoices}
                onOpenInvoice={(id) => { setInvoiceDelivery({}); setStageInvoiceId(id); }}
                items={supplierOrder.items || []}
              />
            )}
          >
            <SupplierOrderMoreDataSection
              values={getSupplierOrderMoreDataInitialValues(supplierOrder)}
              factories={factories}
              canEdit={canManage}
              onSave={saveMoreData}
              saving={moreDataSaving}
              onFactoryCreated={(factory) => setFactories((current) => [...current, factory])}
            />
          </SupplierOrderStagePanel>
        ) : (
          <OrderWorkflowRail
            title={t('orders:supplierOrders.detail.workflow')}
            steps={supplierWorkflowSteps}
            currentKey={currentProgressStatus}
            currentLabel={supplierWorkflowCurrentLabel}
            cancelled={isSupplierWorkflowCancelled}
            cancelledText={t('orders:supplierOrders.detail.cancelledWorkflow')}
            held={isSupplierWorkflowHeld}
            heldText={t('orders:supplierOrders.detail.heldWorkflow')}
          />
        )}
        {supplierOrder.draftSourceOrderId && ((supplierOrder.items?.length ?? 0) > 0 || supplierOrder.sourceOrder?.notes?.trim()) ? (
          <SupplierOrderOriginCard sourceOrderId={supplierOrder.draftSourceOrderId} sourceOrder={supplierOrder.sourceOrder} items={supplierOrder.items || []} />
        ) : null}
        {canViewSupplierPurchasePrices && (supplierOrder.items?.length ?? 0) > 0 ? (
          <Collapse className="zd-costs-collapse" bordered={false} items={[{
            key: 'costs',
            label: t('orders:supplierOrders.deliveries.costs'),
            children: (
              <SupplierOrderCostCard
            supplierOrder={supplierOrder}
            transports={stageTransports ?? null}
            inResult={supplierOrder.status === 'INVOICED'}
            onEditItems={canManage ? () => setLinesOpen(true) : undefined}
            onEditCustoms={canEditSupplierOrderFinance ? () => startEditing('landedCosts') : undefined}
          />
            ),
          }]} />
        ) : null}
        {canManage ? (
          <Modal
            open={linesOpen}
            title={t('orders:supplierOrders.card.linesTitle', { number: displayOrderNumber })}
            footer={null}
            width={920}
            onCancel={() => setLinesOpen(false)}
            destroyOnHidden
            maskClosable={false}
          >
            <SupplierOrderItemsSection
              layout="dialog"
              supplierOrder={supplierOrder}
              canEdit
              canViewSupplierPurchasePrices={canViewSupplierPurchasePrices}
              onSaved={async () => {
                setLinesOpen(false);
                await refresh();
              }}
              onCancel={() => setLinesOpen(false)}
            />
          </Modal>
        ) : null}

      </div>

      {/* „Dopisz koszt odprawy” of the cost card: the landed costs form of the ZD in a dialog. */}
      <Modal
        open={editingField === 'landedCosts'}
        title={t('orders:supplierOrders.detail.landedCosts')}
        onCancel={() => cancelEditing('landedCosts')}
        onOk={() => { void saveField('landedCosts'); }}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingField === 'landedCosts'}
        width={720}
        destroyOnHidden
      >
        {landedCostsForm()}
      </Modal>

      {/* Stage „Zamówione”: the supplier's confirmation sets the ready date and the status. */}
      <Modal
        open={supplierConfirmOpen}
        title={t('orders:supplierOrders.card.supplierConfirm.title')}
        onCancel={() => setSupplierConfirmOpen(false)}
        onOk={() => { void saveSupplierConfirmation(); }}
        okText={t('orders:supplierOrders.card.supplierConfirm.save')}
        cancelText={t('common:cancel')}
        okButtonProps={{ disabled: !supplierConfirmDate, 'data-testid': 'supplier-order-confirm-save' } as any}
        confirmLoading={stageSaving}
        destroyOnClose
      >
        <Space direction="vertical" size={12} style={{ width: '100%' }} data-testid="supplier-order-confirm-dialog">
          <div>
            <label htmlFor="supplier-confirm-date" style={{ display: 'block', marginBottom: 6, fontWeight: 500 }}>
              {t('orders:supplierOrders.card.supplierConfirm.dateLabel')}
            </label>
            <DatePicker
              id="supplier-confirm-date"
              style={{ width: '100%' }}
              value={supplierConfirmDate}
              onChange={(value) => setSupplierConfirmDate(value)}
              format="YYYY-MM-DD"
            />
          </div>
          {supplierOrder.expectedDelivery ? (
            <Text type="secondary">
              {t('orders:supplierOrders.card.supplierConfirm.plannedHint', { date: formatDateEuropean(supplierOrder.expectedDelivery) })}
            </Text>
          ) : null}
        </Space>
      </Modal>

      {/* Task „Faktura zakupu” of the stage panel: the same editor as the „Finanse” tab. */}
      <SupplierOrderPurchaseInvoiceModal
        supplierOrderId={supplierOrder.id}
        invoiceId={stageInvoiceId}
        {...invoiceDelivery}
        onClose={() => { setStageInvoiceId(null); setInvoiceDelivery({}); void refresh(); }}
        onPersisted={() => { void refresh(); }}
      />

      {/* „Historia” from the header menu: the former „Aktywność” tab. */}
      <Modal
        open={historyOpen}
        title={t('orders:supplierOrders.card.history.title', { number: displayOrderNumber })}
        footer={null}
        onCancel={() => setHistoryOpen(false)}
        destroyOnHidden
      >
        {valuationHistoryFailed ? (
          <Alert type="warning" showIcon message={t('orders:supplierOrders.card.history.valuationFailed')} style={{ marginBottom: 12 }} />
        ) : null}
        {historyItems.length === 0 ? (
          <Text type="secondary">{t('orders:supplierOrders.card.history.empty')}</Text>
        ) : (
          <Timeline style={{ marginBottom: 0 }} data-testid="supplier-order-history">
            {historyItems.map((history) => (
              <Timeline.Item key={history.key} data-testid={`supplier-order-history-${history.kind}`}>
                <div>
                  <strong>
                    {history.kind === 'status'
                      ? t(`orders:supplierOrders.status.${history.status}`)
                      : t('orders:supplierOrders.card.history.valuation')}
                  </strong>
                  {history.at && (
                    <div style={{ fontSize: 12, color: 'var(--app-text-muted)' }}>{formatDateTimeEuropean(history.at)}</div>
                  )}
                  {history.by && (
                    <div style={{ fontSize: 12, color: 'var(--app-text-secondary)' }}>{history.by}</div>
                  )}
                  {history.note && <div style={{ fontSize: 12 }}>{history.note}</div>}
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        )}
      </Modal>

      {/* Status modal */}
      <Modal
        title={t('orders:orderDetail.updateStatus')}
        open={statusModalVisible}
        onCancel={() => setStatusModalVisible(false)}
        footer={null}
      >
        <Form form={statusForm} layout="vertical" onFinish={handleChangeStatus}>
          <Form.Item name="status" label={t('orders:supplierOrders.form.status')} rules={[{ required: true }]}>
            <OrderStatusTransitionSelect
              statuses={SUPPLIER_ORDER_STATUSES}
              currentStatus={supplierOrder.status}
              allowedTransitions={allowedStatusTransitions}
              getStatusLabel={(status) => t(`orders:supplierOrders.status.${status}`, {
                defaultValue: t('common:unknownStatus'),
              })}
              currentLabel={t('orders:statusSelection.current')}
              unavailableLabel={t('orders:statusSelection.unavailable')}
            />
          </Form.Item>
          <Form.Item name="statusNotes" label={t('orders:orderDetail.notes')}>
            <Input.TextArea rows={3} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" loading={statusSaving}>
                {t('common:save')}
              </Button>
              <Button onClick={() => setStatusModalVisible(false)}>{t('common:cancel')}</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

    </div>
  );
};

/**
 * One ZD card for every stage (owner 2026-10-05: „Każde zamówienie powinno być
 * tą makietą — różnią się jedynie etapem”): `/app/supplier-orders/new` is the
 * same card in stage „Szkic” before the first save, `/app/supplier-orders/:id`
 * the saved ZD. The former ZD wizard is gone.
 */
const SupplierOrderCard: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return id ? <SupplierOrderDetail /> : <SupplierOrderNewDraftCard />;
};

export default SupplierOrderCard;
